import { useCallback, useEffect, useRef, useState } from "react";
import axiosInstance, { getApiBaseUrl } from "../content/axiosConfig";
import showErrorToast, { showSuccessToast } from "../toast/toast";
import CustomModal from "../custom/customModal";

export const MAX_DETAIL_GALLERY_IMAGES = 30;
const MAX_FILE_BYTES = 5 * 1024 * 1024;
const ACCEPT = "image/jpeg,image/png,image/jpg,.jpg,.jpeg,.png";

export function resolveUploadUrl(pathOrUrl) {
  if (!pathOrUrl) return "";
  const s = String(pathOrUrl).trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s) || s.startsWith("data:")) return s;
  const base = getApiBaseUrl().replace(/\/$/, "");
  return `${base}${s.startsWith("/") ? s : `/${s}`}`;
}

/**
 * Session-level gallery for a sampling detail (PondDID) — not tied to fish samples.
 */
export default function SamplingDetailGallery({
  pondDetailId,
  canEdit = true,
  onChanged,
}) {
  const [images, setImages] = useState([]);
  const [maxImages, setMaxImages] = useState(MAX_DETAIL_GALLERY_IMAGES);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(null);
  const inputRef = useRef(null);

  const remaining = Math.max(0, maxImages - images.length);
  const previewOpen = previewIndex != null && previewIndex >= 0 && previewIndex < images.length;
  const previewSrc = previewOpen ? resolveUploadUrl(images[previewIndex]?.url) : "";
  const previewName = previewOpen
    ? images[previewIndex]?.fileName || `Photo ${previewIndex + 1}`
    : "";

  const loadImages = useCallback(async () => {
    if (!pondDetailId) {
      setImages([]);
      return;
    }
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/api/PondDetail/${pondDetailId}/gallery`);
      setImages(res.data?.images || []);
      if (res.data?.maxImages) setMaxImages(res.data.maxImages);
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to load gallery");
      setImages([]);
    } finally {
      setLoading(false);
    }
  }, [pondDetailId]);

  useEffect(() => {
    loadImages();
    setPreviewIndex(null);
  }, [loadImages]);

  useEffect(() => {
    if (!previewOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setPreviewIndex(null);
      if (e.key === "ArrowLeft") {
        setPreviewIndex((i) => (i == null ? i : (i - 1 + images.length) % images.length));
      }
      if (e.key === "ArrowRight") {
        setPreviewIndex((i) => (i == null ? i : (i + 1) % images.length));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [previewOpen, images.length]);

  const notifyChanged = (imageCount) => {
    if (typeof onChanged === "function") {
      onChanged({ pondDetailId, imageCount });
    }
  };

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length || !pondDetailId) return;

    if (remaining <= 0) {
      showErrorToast(`Maximum of ${maxImages} images in this gallery`);
      return;
    }

    const accepted = [];
    for (const file of files) {
      const okType =
        /image\/(jpeg|jpg|png)/i.test(file.type) ||
        /\.(jpe?g|png)$/i.test(file.name || "");
      if (!okType) {
        showErrorToast(`${file.name || "File"}: only JPEG and PNG are allowed`);
        continue;
      }
      if (file.size > MAX_FILE_BYTES) {
        showErrorToast(`${file.name || "File"}: must be 5 MB or smaller`);
        continue;
      }
      accepted.push(file);
    }

    const toUpload = accepted.slice(0, remaining);
    if (!toUpload.length) return;
    if (accepted.length > remaining) {
      showErrorToast(`Only ${remaining} more image(s) can be added (limit ${maxImages})`);
    }

    setUploading(true);
    const formData = new FormData();
    toUpload.forEach((f) => formData.append("images", f));
    try {
      const res = await axiosInstance.post(`/api/PondDetail/${pondDetailId}/gallery`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (Array.isArray(res.data?.images)) {
        setImages(res.data.images);
      } else {
        await loadImages();
      }
      showSuccessToast(
        toUpload.length === 1 ? "Image uploaded" : `${toUpload.length} images uploaded`
      );
      notifyChanged(res.data?.imageCount ?? toUpload.length);
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to upload images");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleDelete = async (imageId, index) => {
    if (!canEdit || !pondDetailId) return;
    try {
      const res = await axiosInstance.delete(
        `/api/PondDetail/${pondDetailId}/gallery/${imageId}`
      );
      const next = res.data?.images || [];
      setImages(next);
      notifyChanged(res.data?.imageCount ?? 0);
      setPreviewIndex((prev) => {
        if (prev == null) return null;
        if (next.length === 0) return null;
        if (index != null && prev > index) return prev - 1;
        if (prev >= next.length) return next.length - 1;
        return prev;
      });
      showSuccessToast("Image removed");
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to delete image");
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (!canEdit || uploading || remaining <= 0) return;
    uploadFiles(e.dataTransfer?.files);
  };

  if (!pondDetailId) {
    return (
      <div className="donezo-empty-state donezo-empty-state--compact">
        <p className="donezo-empty-state__text mb-0">No sampling session selected.</p>
      </div>
    );
  }

  return (
    <div className="sampling-detail-gallery">
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <div className="sampling-detail-gallery__label">
          Gallery{" "}
          <span className="donezo-status-dot donezo-status-dot--other ms-1">
            <span className="donezo-status-dot__mark" aria-hidden />
            {images.length}/{maxImages}
          </span>
        </div>
        {loading && <span className="small text-muted">Loading…</span>}
        {uploading && <span className="small text-muted">Uploading…</span>}
      </div>

      {canEdit && remaining > 0 && (
        <div
          role="button"
          tabIndex={0}
          className={`sampling-image-dropzone mb-3 ${dragOver ? "is-dragover" : ""} ${
            uploading ? "is-disabled" : ""
          }`}
          onDragEnter={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            setDragOver(false);
          }}
          onDrop={onDrop}
          onClick={() => !uploading && inputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              inputRef.current?.click();
            }
          }}
        >
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            multiple
            className="d-none"
            disabled={uploading}
            onChange={(e) => uploadFiles(e.target.files)}
          />
          <div className="text-center px-2 py-4">
            <i className="fa fa-cloud-upload-alt fa-2x sampling-image-dropzone__icon mb-2 d-block" aria-hidden />
            <div className="fw-medium sampling-image-dropzone__title">
              Drop images here or click to browse
            </div>
            <div className="small text-muted mt-1">
              JPEG or PNG · up to 5 MB each · {remaining} slot{remaining === 1 ? "" : "s"} left
            </div>
          </div>
        </div>
      )}

      {canEdit && remaining <= 0 && (
        <p className="small text-muted mb-2">
          Gallery limit reached ({maxImages}). Remove one to add more.
        </p>
      )}

      {!loading && images.length === 0 && (
        <div className="donezo-empty-state donezo-empty-state--compact">
          <div className="donezo-empty-state__icon" aria-hidden>
            <i className="fa fa-images" />
          </div>
          <p className="donezo-empty-state__title">No photos yet</p>
          <p className="donezo-empty-state__text mb-0">
            Upload session photos for this sampling visit.
          </p>
        </div>
      )}

      {images.length > 0 && (
        <div className="sampling-image-grid">
          {images.map((img, index) => {
            const src = resolveUploadUrl(img.url);
            return (
              <div key={img.id} className="sampling-image-tile">
                <button
                  type="button"
                  className="sampling-image-tile__thumb"
                  onClick={() => setPreviewIndex(index)}
                  title="View larger"
                >
                  <img src={src} alt={img.fileName || `Gallery ${index + 1}`} />
                </button>
                {canEdit && (
                  <button
                    type="button"
                    className="btn btn-sm btn-danger sampling-image-tile__delete"
                    title="Remove image"
                    aria-label="Remove image"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(img.id, index);
                    }}
                  >
                    <i className="fa fa-trash" aria-hidden />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <CustomModal
        isOpen={previewOpen}
        onClose={() => setPreviewIndex(null)}
        title={
          <span className="d-inline-flex align-items-center gap-2">
            <i className="fa fa-images text-success" aria-hidden />
            <span>
              {previewName}
              {images.length > 1 ? (
                <span className="text-muted fw-normal ms-2 small">
                  ({previewIndex + 1} of {images.length})
                </span>
              ) : null}
            </span>
          </span>
        }
        showButton
        showCancelButton
        showSubmitButton={false}
        modalSize="modal-xl"
        minHeight="auto"
        maxHeight="90vh"
      >
        <div className="sampling-gallery-lightbox">
          {images.length > 1 && (
            <button
              type="button"
              className="sampling-gallery-lightbox__nav sampling-gallery-lightbox__nav--prev"
              onClick={() =>
                setPreviewIndex((i) => (i - 1 + images.length) % images.length)
              }
              aria-label="Previous image"
            >
              <i className="fa fa-chevron-left" aria-hidden />
            </button>
          )}

          <div className="sampling-gallery-lightbox__stage">
            {previewSrc ? (
              <img src={previewSrc} alt={previewName || "Gallery preview"} />
            ) : (
              <p className="text-muted mb-0">Image unavailable</p>
            )}
          </div>

          {images.length > 1 && (
            <button
              type="button"
              className="sampling-gallery-lightbox__nav sampling-gallery-lightbox__nav--next"
              onClick={() => setPreviewIndex((i) => (i + 1) % images.length)}
              aria-label="Next image"
            >
              <i className="fa fa-chevron-right" aria-hidden />
            </button>
          )}
        </div>

        {images.length > 1 && (
          <div className="sampling-gallery-lightbox__filmstrip" role="list">
            {images.map((img, index) => {
              const src = resolveUploadUrl(img.url);
              return (
                <button
                  key={img.id}
                  type="button"
                  role="listitem"
                  className={`sampling-gallery-lightbox__film${
                    index === previewIndex ? " is-active" : ""
                  }`}
                  onClick={() => setPreviewIndex(index)}
                  title={img.fileName || `Photo ${index + 1}`}
                >
                  <img src={src} alt="" />
                </button>
              );
            })}
          </div>
        )}
      </CustomModal>
    </div>
  );
}
